import {
  Accordion,
  AccordionVariant,
  FormMessage,
  FormMessageVariant,
  RadioToggleGroup,
  SideCard,
  SliderComponent,
} from '@aus-platform/design-system';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { isNil } from 'lodash';
import {
  selectMap3DState,
  selectWorkspaceActionableLayers,
  updateWorkspaceLayer,
} from 'map-3d/shared/map-3d-slices';
import { type ChangeEvent, type ReactNode, useEffect, useMemo } from 'react';
import { LayerType } from 'shared/api';
import { Histogram, OpacitySlider, viridis2ColorMap } from 'shared/components';
import { formatNumberUS } from 'shared/helpers';
import { DateTimeFormatLength } from 'shared/utils';
import { type WorkspaceLayerListObj } from '../../../../shared';
import { initialPointCloudData, removeMapLayer } from '../../shared';
import { type WorkspaceSidecardProps } from '../types';
import {
  INITIAL_CVTILES_OPACITY,
  MAX_POINT_SIZE,
  pointShapeOptions,
} from './constants';
import { AccordionEventKey, PointShape } from './enums';
import { canLayerShowHistogram } from './helpers';

export const LayerPropertiesSidecard: React.FC<WorkspaceSidecardProps> = ({
  show,
  onClose,
}) => {
  // Dispatchers.
  const dispatch = useAppDispatch();

  // Selectors.
  const { currentPropertyLayer: layer } = useAppSelector(
    selectWorkspaceActionableLayers,
  );
  const { cesiumProxy } = useAppSelector(selectMap3DState);

  const cvTilesTool = useMemo(
    () => cesiumProxy?.cvTilesTools.cvTilesTool,
    [cesiumProxy],
  );

  // Handlers.
  const updateLayer = (layers: WorkspaceLayerListObj) => {
    dispatch(updateWorkspaceLayer(layers));
  };

  // UseEffects.
  useEffect(() => {
    if (layer?.type === LayerType.PointCloud && !layer.pointCloudData) {
      const newPointCloudData = initialPointCloudData;

      updateLayer({
        [layer.id]: {
          ...layer,
          pointCloudData: newPointCloudData,
        },
      });
    }
  }, [layer?.id, layer?.type, layer?.pointCloudData]);

  // Initialize point cloud properties when mapLayer becomes available
  useEffect(() => {
    if (
      layer?.type === LayerType.PointCloud &&
      layer.pointCloudData &&
      layer.mapLayer &&
      cesiumProxy
    ) {
      // Select the point cloud layer first, then apply all properties
      cesiumProxy.layerManager.selectPointCloudLayer(layer.id);

      cesiumProxy.layerManager.setPointCloudOpacity(
        layer.pointCloudData.opacity ?? initialPointCloudData.opacity,
      );
      cesiumProxy.layerManager.setPointCloudPointSize(
        layer.pointCloudData.pointSize ?? initialPointCloudData.pointSize,
      );
      if (layer.pointCloudData?.pointShape) {
        const isCircle = layer.pointCloudData.pointShape === PointShape.Circle;
        cesiumProxy.layerManager.setPointCloudPointShape(isCircle);
      }
    }
  }, [layer?.mapLayer, layer?.pointCloudData, cesiumProxy, layer?.id]);

  const onChangeHistogramRescale = (newRescale: string) => {
    if (isNil(layer) || !layer.histogramData) {
      return;
    }

    let { mapLayer } = layer;

    // Create new map layer if rescale value is changed.
    if (newRescale !== layer.histogramData.rescale && cesiumProxy) {
      // Remove previous map layer if it exists.
      if (layer.mapLayer) {
        removeMapLayer(cesiumProxy, layer.mapLayer);
      }

      // Add new map layer.
      mapLayer = cesiumProxy.layerManager.addHistogramLayerToCesium(
        {
          ...layer.histogramData,
          rescale: newRescale,
        },
        {
          show: true,
          zIndex: layer.cesiumIndex,
        },
      );

      updateLayer({
        [layer.id]: {
          ...layer,
          mapLayer,
          histogramData: {
            ...layer.histogramData,
            rescale: newRescale,
          },
        },
      });
    }
  };

  const onChangeHistogramOpacity = (newOpacity: number) => {
    if (isNil(layer)) {
      return;
    }
    if (!newOpacity) {
      newOpacity = 0;
    }
    if (newOpacity >= 100) {
      newOpacity = 100;
    }
    const { mapLayer, histogramData } = layer;

    // Update the existing map layer if only opacity is changed.
    if (
      newOpacity !== layer.histogramData?.opacity &&
      mapLayer?.show &&
      'alpha' in mapLayer
    ) {
      mapLayer.alpha = parseFloat(newOpacity.toString()) / 100.0;
      cesiumProxy?.viewer.scene.requestRender();
    }

    updateLayer({
      [layer.id]: {
        ...layer,
        histogramData: {
          ...histogramData,
          opacity: newOpacity,
        },
      },
    });
  };

  const onChangePointCloudOpacity = (newOpacity: number) => {
    if (isNil(layer)) {
      return;
    }
    if (!newOpacity) {
      newOpacity = 0;
    }
    if (newOpacity >= 100) {
      newOpacity = 100;
    }
    const { pointCloudData } = layer;

    // Update the point cloud opacity through the layer manager
    if (
      newOpacity !== layer.pointCloudData?.opacity &&
      cesiumProxy &&
      layer.type === LayerType.PointCloud
    ) {
      // Select the point cloud layer first, then apply opacity
      cesiumProxy.layerManager.selectPointCloudLayer(layer.id);
      cesiumProxy.layerManager.setPointCloudOpacity(newOpacity);
    }

    updateLayer({
      [layer.id]: {
        ...layer,
        pointCloudData: {
          ...pointCloudData,
          opacity: newOpacity,
        },
      },
    });
  };

  const onChangeCVTilesOpacity = (newOpacity: number) => {
    if (isNil(layer) || isNil(cvTilesTool)) {
      return;
    }

    cvTilesTool.setOpacity(layer.id, newOpacity / 100);
    updateLayer({
      [layer.id]: {
        ...layer,
        cvTilesData: {
          ...layer.cvTilesData,
          opacity: newOpacity,
        },
      },
    });
  };

  const onChangePointSize = (value: number | number[]) => {
    if (isNil(layer)) {
      return;
    }
    const newPointSize = Array.isArray(value) ? value[0] : value;
    const { pointCloudData } = layer;

    // Update the point cloud point size through the layer manager
    if (
      newPointSize !== layer.pointCloudData?.pointSize &&
      cesiumProxy &&
      layer.type === LayerType.PointCloud
    ) {
      // Select the point cloud layer first, then apply point size
      cesiumProxy.layerManager.selectPointCloudLayer(layer.id);
      cesiumProxy.layerManager.setPointCloudPointSize(newPointSize);
    }

    updateLayer({
      [layer.id]: {
        ...layer,
        pointCloudData: {
          ...pointCloudData,
          pointSize: newPointSize,
        },
      },
    });
  };

  const onChangePointShape = (event: ChangeEvent<HTMLInputElement>) => {
    if (isNil(layer)) {
      return;
    }
    const newPointShape = event.target.title as PointShape;
    const isCircle = newPointShape === PointShape.Circle;
    const { pointCloudData } = layer;

    if (
      newPointShape !== layer.pointCloudData?.pointShape &&
      cesiumProxy &&
      layer.type === LayerType.PointCloud
    ) {
      // Select the point cloud layer first, then apply point shape
      cesiumProxy.layerManager.selectPointCloudLayer(layer.id);
      cesiumProxy.layerManager.setPointCloudPointShape(isCircle);
    }

    updateLayer({
      [layer.id]: {
        ...layer,
        pointCloudData: {
          ...pointCloudData,
          pointShape: newPointShape,
        },
      },
    });
  };

  const getCadLayerInfoText = (): ReactNode | null => {
    if (isNil(layer?.properties)) {
      return null;
    }

    const { totalEntities, unsupportedEntityCount } = layer.properties;

    if (isNil(totalEntities) || isNil(unsupportedEntityCount)) {
      return null;
    }

    const total = formatNumberUS(totalEntities);
    const unsupported = formatNumberUS(unsupportedEntityCount);
    const entityLabel = totalEntities === 1 ? 'entity' : 'entities';
    const unsupportedEntityLabel = unsupportedEntityCount === 1 ? 'is' : 'are';

    if (!isNil(unsupported)) {
      return (
        <span>
          This CAD file contains <b>{total}</b> {entityLabel}, of which{' '}
          <b>{unsupported}</b> {unsupportedEntityLabel} not supported for
          visualisation.
        </span>
      );
    }

    return (
      <span>
        This CAD file contains a total of <b>{total}</b> {entityLabel}.
      </span>
    );
  };

  // Renders.
  return (
    <SideCard
      className="layer-properties-sidecard"
      title="Layer Properties"
      show={show}
      onClose={onClose}
      showCloseButton={true}
      backdrop={false}
    >
      <Accordion
        className="layer-properties-sidecard__accordion"
        alwaysOpen
        variant={AccordionVariant.ChevronRight}
        defaultActiveKey={Object.values(AccordionEventKey)}
      >
        {layer && canLayerShowHistogram(layer) && (
          <>
            <Accordion.Item
              title="Opacity"
              eventKey={AccordionEventKey.Opacity}
            >
              <div className="accordion__group">
                <OpacitySlider
                  opacity={layer.histogramData?.opacity ?? 0}
                  onChangeOpacity={onChangeHistogramOpacity}
                />
              </div>
            </Accordion.Item>
            <Accordion.Item
              title="Elevation Profile Histogram"
              eventKey={AccordionEventKey.Histogram}
            >
              <div className="accordion__group">
                <Histogram
                  colorMap={viridis2ColorMap}
                  metadata={layer?.histogramData?.metadata}
                  onChangeHistogramRescale={onChangeHistogramRescale}
                  rescale={layer.histogramData?.rescale}
                />
              </div>
            </Accordion.Item>
          </>
        )}
        {layer?.type === LayerType.PointCloud && (
          <>
            <Accordion.Item
              title="Opacity"
              eventKey={AccordionEventKey.Opacity}
            >
              <div className="accordion__control-group">
                <OpacitySlider
                  opacity={
                    layer?.pointCloudData?.opacity ??
                    initialPointCloudData.opacity
                  }
                  onChangeOpacity={onChangePointCloudOpacity}
                />
              </div>
            </Accordion.Item>
            <Accordion.Item
              title="Point Size"
              eventKey={AccordionEventKey.PointSize}
            >
              <div className="accordion__control-group">
                <SliderComponent
                  value={
                    layer?.pointCloudData?.pointSize ??
                    initialPointCloudData.pointSize
                  }
                  max={MAX_POINT_SIZE}
                  step={0.01}
                  onChange={onChangePointSize}
                  tipProps={{ visible: false }}
                  showValues
                />
              </div>
            </Accordion.Item>
            <Accordion.Item
              title="Point Shape"
              eventKey={AccordionEventKey.PointShape}
            >
              <div className="accordion__control-group">
                <RadioToggleGroup
                  radioOptions={pointShapeOptions}
                  onChange={onChangePointShape}
                  checked={(option) => {
                    return (
                      option.value ===
                      (layer.pointCloudData?.pointShape ?? PointShape.Square)
                    );
                  }}
                />
              </div>
            </Accordion.Item>
          </>
        )}
        {layer?.type === LayerType.Cad && (
          <Accordion.Item title="Opacity" eventKey={AccordionEventKey.Opacity}>
            <div className="accordion__control-group">
              <OpacitySlider
                opacity={layer.cvTilesData?.opacity ?? INITIAL_CVTILES_OPACITY}
                onChangeOpacity={onChangeCVTilesOpacity}
              />
            </div>
          </Accordion.Item>
        )}
        <Accordion.Item
          title="Layer Details"
          eventKey={AccordionEventKey.LayerDetails}
        >
          <div className="accordion__group">
            <div className="accordion__group__header">{layer?.name}</div>
          </div>
          {layer?.type === LayerType.Cad && (
            <div className="accordion__group">
              <div className="accordion__group__content">
                <FormMessage
                  variant={FormMessageVariant.Info}
                  message={getCadLayerInfoText()}
                />
              </div>
            </div>
          )}
          <div className="accordion__group accordion__group--separated">
            <div className="accordion__group__header">Project</div>
            <div className="accordion__group__content">
              {layer?.project.name}
            </div>
          </div>
          <div className="accordion__group">
            <div className="accordion__group__header">Site</div>
            <div className="accordion__group__content">{layer?.site.name}</div>
          </div>
          <div className="accordion__group">
            <div className="accordion__group__header">Iteration</div>
            <div className="accordion__group__content">
              {layer?.iteration.name}
            </div>
          </div>
          <div className="accordion__group accordion__group--separated">
            <div className="accordion__group__content">Created On</div>
            <div className="accordion__group__content">
              {layer?.createdAt.formatDateTime?.(
                DateTimeFormatLength.Long,
                DateTimeFormatLength.Medium,
              )}
            </div>
          </div>
        </Accordion.Item>
      </Accordion>
    </SideCard>
  );
};
